import React, { useState, useEffect } from 'react';
import { Select, Table, Toolbar, TextLink, ProgressBar, AlertVariant, Button, PlusFilledIcon, TextInput, Searchbar } from 'react-unity';
import SectionHomeWrapper from '../common/wrappers/SectionHomeWrapper';
import PortOpeningRequestOnPrem from '../../models/entities/PortOpeningRequestOnPrem';
import OnPremPortOpeningRequestService from '../../services/OnPremPortOpeningRequestService';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import AlertModal from '../common/modals/AlertModal';
import Paginator from '../common/tables/Paginator';
import './OnPremPortOpeningRequestsHome.css';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import CheckboxFilter, { CheckboxOption } from '../common/tables/CheckboxFilter';
import Filter from '../common/tables/Filter';
import styled from 'styled-components';
import WorkflowStateBadge from '../common/WorkflowStateBadge';
import { Params, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { authenticatedUser } from '../../authentication/authModule';
import { UserRole } from '../../models/enums/UserRole';
import { WorkflowState } from '../../models/enums/WorkflowState';
import Memoized from '../common/Memoized';
import DateRangePicker from '../common/DateRangePicker';

export type PORFilters = {
	scope: CheckboxOption[];
	status: CheckboxOption[];
	searchUser: string;
	orderBy: string;
	fromDate: string;
	toDate: string;
};

const StyledButton = styled(Button)`
	width: 120px;
	height: 34px;
`;

const MyP = styled.p`
	margin-right: 8px;
	margin-bottom: auto !important;
	margin-top: auto !important;
`;

const StyledItem = styled(Toolbar.Item)`
	width: 50%;
`;

const MyLink = styled.span`
	color: #0c69b0;
	text-decoration: underline;
	cursor: pointer;
`;

interface PortOpeningRequestsHomeProps extends PortOpeningRequestsHomeWithParamsProps { }

interface PortOpeningRequestsHomeWithParamsProps {
	scopes?: PortOpeningRequestsDirection[];
	subtitle?: string;
	navigate?: any;
	params?: Params<string>;
}

interface PortOpeningRequestsHomeWithParamsState {
	loading: boolean;
	requests: PortOpeningRequestOnPrem[];
	filteredRequests: PortOpeningRequestOnPrem[];
	endModal: AlertBanner;
	newConnectedPORInfoModalVisible: boolean;

	visibleRequests: PortOpeningRequestOnPrem[];
}

interface PORByRequestFilterProps {
	pors: PortOpeningRequestOnPrem[];
	onFilterChange: any;
}

export type OnPremPORsMemoizedData = {
	searchUser: void,
	status: void,
	scope: void,
	orderBy: void,
	fromDate: void,
	toDate: void
}

const defaultMemoizedDataStructure = {
	searchUser: undefined,
	orderBy: undefined,
	scope: undefined,
	status: undefined,
	fromDate: undefined,
	toDate: undefined
} as OnPremPORsMemoizedData


const memoized = new Memoized(defaultMemoizedDataStructure)

class PortOpeningRequestsHomeWithParams extends
	React.Component<PortOpeningRequestsHomeWithParamsProps, PortOpeningRequestsHomeWithParamsState> {

	OnPremPortOpeningRequestService: OnPremPortOpeningRequestService;

	filter: Filter<PORFilters>;

	constructor(props) {
		super(props);
		this.OnPremPortOpeningRequestService = new OnPremPortOpeningRequestService();
		this.state = {
			loading: true,
			requests: [],
			filteredRequests: [],
			visibleRequests: [],
			endModal: {
				visible: false,
			},
			newConnectedPORInfoModalVisible: false,
		};

		this.filter = new Filter(this.filterPORs, this.getFilters());
	}

	async componentDidMount() {
		this.loadRequests();
	}

	loadRequests = async () => {
		try {
			let requests = [];
			this.setState({ loading: true });
			requests = await this.OnPremPortOpeningRequestService.getAll();
			
			requests = (authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer) || authenticatedUser.isInRole(UserRole.PORToOnPremTechImplementer) || authenticatedUser.isInRole(UserRole.SuperAdmin)) ? requests.map(r => new PortOpeningRequestOnPrem(r)).reverse().filter(request => this.props.scopes.includes(request.direction)) :
			requests.map(r => new PortOpeningRequestOnPrem(r)).reverse().filter(request => this.props.scopes.includes(request.direction) && 
			(request.workflowInstance.createdBy.id===authenticatedUser.userId || (request.approvingUser.id===authenticatedUser.userId && request.workflowInstance.currentWorkflowInstanceState.workflowState===WorkflowState.PendingForDOAGApproval)));

			this.setState({
				requests,
				loading: false,
			}, this.filter.filter);

		} catch (error) {
			this.setState({
				loading: false,
			});
			this.handleEndModal('Something went wrong, try again later.', 'error', 5000);
		}
	};

	handleEndModal = (text: string, variant: AlertVariant, timeout: number) => {
		this.setState({
			endModal: {
				visible: true,
				text,
				variant,
			}
		},
			() => {
				setTimeout(() => {
					this.redirectToSubscriptions();
				}, timeout);
			});
	};

	redirectToSubscriptions = () => {
		this.setState({
			endModal: { visible: false }
		},
			() => {
				this.props.navigate('/');
			});
	};

	getDefaultFilters = () => {
		const defaultFilters = {
			searchUser: '',
			orderBy: 'newest',
			status: [
				{ label: 'Submitted', value: 'Submitted', checked: true },
				{ label: 'Rejected', value: 'Rejected', checked: true },
				{ label: 'Pending for DOAG Approval', value: 'Pending for DOAG Approval', checked: true },				
				{ label: 'Awaiting Implementation', value: 'Awaiting Implementation', checked: true },
				{ label: 'Completed', value: 'Completed', checked: true },
				{ label: 'Cancelled', value: 'Cancelled', checked: true }
			],
			scope:
			[
				{ label: 'ChinaDomain -> Connection', value: PortOpeningRequestsDirection.DomainToConnection.name, checked: true },
				{ label: 'ChinaDomain -> Internet', value: PortOpeningRequestsDirection.DomainToInternet.name, checked: true },
				{ label: 'Proxy/iBoss -> Internet', value: PortOpeningRequestsDirection.DomainProxyToInternet.name, checked: true }
			],
			fromDate: undefined,
			toDate: undefined
		} as PORFilters;

		return defaultFilters;
	};

	getFilters = () => {
		const defaultFilters = this.getDefaultFilters()

		const filters = {
			searchUser: memoized.getMemoizedData("searchUser", defaultFilters.searchUser),
			status: memoized.getMemoizedData("status", defaultFilters.status),
			scope: memoized.getMemoizedData("scope", defaultFilters.scope),
			orderBy: memoized.getMemoizedData("orderBy", defaultFilters.orderBy),
			fromDate: memoized.getMemoizedData("fromDate", defaultFilters.fromDate),
			toDate: memoized.getMemoizedData("toDate", defaultFilters.toDate)
		} as PORFilters;

		return filters;
	}

	allFiltersChecked = (key: string) => {
		return this.getFilters()[key].reduce((acc, box) => acc && box.checked, true);
	}

	saveFilters = () => {
		memoized.saveMemoizeData("status", this.filter.filters.status);
		memoized.saveMemoizeData("searchUser", this.filter.filters.searchUser)
		memoized.saveMemoizeData("orderBy", this.filter.filters.orderBy)
		memoized.saveMemoizeData("scope", this.filter.filters.scope)
		memoized.saveMemoizeData("fromDate", this.filter.filters.fromDate)
		memoized.saveMemoizeData("toDate", this.filter.filters.toDate)
		
	}


	filterPORs = () => {
		let filteredRequest = [];
		this.saveFilters()
		const filterCallback = (por: PortOpeningRequestOnPrem) => {
			return this.filter.filterCheckBox('status', por.workflowInstance?.currentWorkflowInstanceState.workflowState?.name)
				&& this.filter.filterCheckBox('scope', por.direction?.name)
				&& this.filter.isInDateRange("fromDate", "toDate", new Date(por.workflowInstance.createdDate))
				&& (this.filter.filterSearchText('searchUser', por.workflowInstance.createdBy.displayName)
					|| this.filter.filterSearchText('searchUser', por.contactUser?.displayName));
		};

		
		filteredRequest = this.state.requests.filter(filterCallback).sort((a, b) => {
			if (this.filter.filters.orderBy == undefined || this.filter.filters.orderBy === 'newest') {
				return a.id < b.id ? 1 : -1;
			}

			return a.id > b.id ? 1 : -1;
		});
		

		this.setState({
			filteredRequests: filteredRequest,
			visibleRequests: filteredRequest
		});

	};

	goToNewRequest = () => {
		this.props.navigate(
			'/onPremportOpeningRequests/new'
		);
	};

	goToPORDetails = (request) => {
		this.props.navigate(
			`/onPremportOpeningRequests/${request.id}`
		);
	};

	clearSearchbar = (event) => {
		event.preventDefault();
		this.filter.updateFilter('', 'searchUser');
	};

	render() {
		const rows = this.state.visibleRequests?.map(request => (
			<Table.Body.Row key={request.id} onClick={() => this.goToPORDetails(request)} style={{ cursor: "pointer" }}>
				<Table.Body.Cell>					
					{request.id}
				</Table.Body.Cell>
				<Table.Body.Cell>
					{request.direction.displayName}
				</Table.Body.Cell>
				<Table.Body.Cell>
				<WorkflowStateBadge
							workflowState={request.workflowInstance.currentWorkflowInstanceState.workflowState}
							fullWidth
						/>
				</Table.Body.Cell>
				<Table.Body.Cell>
					{request.workflowInstance.createdBy.displayName}
				</Table.Body.Cell>
				<Table.Body.Cell>
					{request.approvingUser?.displayName}
				</Table.Body.Cell>
				<Table.Body.Cell>
					{moment(new Date(request.workflowInstance.createdDate)).format('YYYY-MM-DD')}
				</Table.Body.Cell>
				<Table.Body.Cell>
					<StyledButton onClick={() => this.goToPORDetails(request)} variant="primary">
						See Details
					</StyledButton>
				</Table.Body.Cell>
			</Table.Body.Row>
		));

		return (
			<>
				<SectionHomeWrapper
					title={`Port Opening Requests${this.props.subtitle ? `: ${this.props.subtitle}` : ''}`}
					//linkTree={this.getLinkTree()}
				>
					<p>
						For more information about PORs {' '}
						<TextLink className='em-u-margin-right-none' external target="_blank" href='http://cctrum100.exxonmobil.com.cn/Documents/ENG/SSP%20Portal%20POR%20User%20Guide%20Process%20Users%20Policy.pdf'>
							Click here
						</TextLink>
					</p>
					{this.state.loading ?
						<ProgressBar
							label="Loading requests..."
							className="em-u-margin-top-half"
							indeterminate
							hideValueLabel
						/> :
						<>
							<Table
								header={
									<><Toolbar>
										<StyledItem>
											<Searchbar
												condensed
												buttonProps={{ onClick: this.clearSearchbar }}
												inputProps={{
													value: this.filter.filters.searchUser,
													onChange: (event) => this.filter.handleSearchTextChange(event, 'searchUser'),
													placeholder: 'Search Requestor',
												}} />
										</StyledItem>
										<Toolbar.Item right>
											<Button
												variant="primary"
												size="small"
												onClick={() => {
													this.goToNewRequest();
												}}
											>
												<PlusFilledIcon size="small" />
												<span>New Request</span>
											</Button>
										</Toolbar.Item>
									</Toolbar>
										<Toolbar>
											<Toolbar.Item>
												<DateRangePicker
													toDateOnChangeFunction={(e) => this.filter.handleSearchTextChange(e, 'toDate')}
													fromDateOnChangeFunction={(e) => this.filter.handleSearchTextChange(e, 'fromDate')}
													fromDateValue={this.filter.filters.fromDate}
													toDateValue={this.filter.filters.toDate}
													containerStyle={{ position: "relative", bottom: "8px" }}
													pickerStyle={{ margin: "0px" }}
												/>
											</Toolbar.Item>
											<Toolbar.Item>
												<CheckboxFilter
													label="Scope"
													buttonProps={{ size: 'small' }}
													onChange={(event) => this.filter.handleCheckboxChange(event, 'scope')}
													options={this.filter.filters.scope} />
											</Toolbar.Item>
											<Toolbar.Item>
												<CheckboxFilter
													label="Status"
													onChange={(event) => this.filter.handleCheckboxChange(event, 'status')}
													options={this.filter.filters.status} />
											</Toolbar.Item>
											<Toolbar.Item>
												<MyLink onClick={() => this.filter.updateFilters(this.getDefaultFilters())}>Clear filters</MyLink>
											</Toolbar.Item>
											<Toolbar.Item right>
												<Button.Group>
													<MyP>Sort by</MyP>
													<Select
														style={{ marginBottom: -12 }}
														value={this.filter.filters.orderBy}
														//onChange={(e) => this.filter.handleSearchTextChange(e, 'orderBy')}
														onChange={(event) => { this.filter.handleSearchTextChange(event, 'orderBy');  }}
														
													>
														<option className='em-c-field-option' value="newest">Newest</option>
														<option className='em-c-field-option' value="oldest">Oldest</option>
													</Select>
												</Button.Group>
											</Toolbar.Item>
										</Toolbar></>
								}
								footer={
									<Paginator
										data={this.state.filteredRequests}
										onPageChange={(page) => {
											this.setState({
												visibleRequests: page
											});
										}}
									/>
								}
							>
								<Table.Head>
									<Table.Head.Row>
										<Table.Head.Cell> POR # </Table.Head.Cell>
										<Table.Head.Cell> Scope </Table.Head.Cell>
										<Table.Head.Cell> Status </Table.Head.Cell>
										<Table.Head.Cell> Requestor </Table.Head.Cell>										
										<Table.Head.Cell> Approver </Table.Head.Cell>
										<Table.Head.Cell> Requested Date </Table.Head.Cell>
										<Table.Head.Cell></Table.Head.Cell>
									</Table.Head.Row>
								</Table.Head>
								<Table.Body>
									{rows}
								</Table.Body>
							</Table>
						</>}
				</SectionHomeWrapper>
				<AlertModal
					{...this.state.endModal}
					willTimeout={false}
					onClose={this.redirectToSubscriptions}
				/>				
			</>
		);
	}
}

const OnPremPortOpeningRequestsHome = (props: PortOpeningRequestsHomeProps) => {

	return <PortOpeningRequestsHomeWithParams scopes={props.scopes} subtitle={props.subtitle} params={useParams()} navigate={useNavigate()} />;
}

export default OnPremPortOpeningRequestsHome;
